// @flow
import { css } from 'styled-components';
import { range } from 'utils/functional';

export const bp = [576, 768, 992, 1280];

export const breakpoints: Array<string> = bp.map(
  (item: number): string => `${item}px`
);

export const dashboardSidebarWidth = 496;
export const dashboardMainWidth = 992;

export const maxWidth = [null, null, 1000, 1248];

export const space = [0, 4, 8, 16, 32, 64, 128];

export const gutterWidth = 48;

export const sectionPadding = ['40px 0 48px', null, '60px 0 72px', null];

export const mediaRetina =
  '@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)';

export const fontSizes = [12, 14, 16, 20, 24, 32, 48, 64, 72, 96];

export const fontSizesByType = {
  h1: [30, null, 40, 48],
  h2: [26, null, 32, 36],
  h3: [22, null, 24],
  h4: [18, null, 20],
  h5: [14, null, 16],
  h6: [12, null, 14],
  headingSmall: [12],
  bodyLarge: [24, null, 30],
  body: [18, null, 20],
  bodySmall: [14, null, 16],
  bodyMicro: [12, null, 14],
  input: [18, null, 18]
};

export const fontWeights = {
  normal: 400,
  semiBold: 600,
  bold: 700
};

export type Color =
  | 'black'
  | 'white'
  | 'fog'
  | 'platinum'
  | 'offWhite'
  | 'aluminum'
  | 'silver'
  | 'coolGrey'
  | 'middleGrey'
  | 'battleshipGrey'
  | 'greyishBrown'
  | 'charcoalGrey'
  | 'waterBlue'
  | 'niceBlue'
  | 'turtleGreen'
  | 'dandelion'
  | 'scarlet'
  | 'lightGrey'
  | 'steelGrey'
  | 'navy'
  | 'oceanBlue'
  | 'skyBlue'
  | 'babyBlue'
  | 'lemon'
  | 'poppy'
  | 'brick';

export const colors: { [Color]: string } = {
  black: '#000000',
  white: '#ffffff',
  fog: '#fbfbfb', // site background
  platinum: '#f8f6f6', // header search background
  lightGrey: '#f4f5f5',
  offWhite: '#f1f1f1',
  aluminum: '#e6e6e6', // some dividers
  silver: '#c4c8cc',
  coolGrey: '#b0b4b9',
  middleGrey: '#787878',
  battleshipGrey: '#717375',
  greyishBrown: '#484848',
  charcoalGrey: '#242424',
  waterBlue: '#0d73d9',
  niceBlue: '#0f5cb5',
  turtleGreen: '#68bd53',
  dandelion: '#f9de09',
  scarlet: '#d0021b',

  // secondary colors
  steelGrey: '#6F6F6F', // accessible battleshipGrey for light grey backgrounds
  navy: '#003965',
  oceanBlue: '#0c70d4', // accessible waterBlue for light grey backgrounds
  skyBlue: '#1d91e9',
  babyBlue: '#5dc2f1',
  lemon: '#f7fc26',
  poppy: '#ef444d',
  brick: '#b40016'
};

export const transition = '0.3s cubic-bezier(0.215, 0.61, 0.355, 1)';

export const fonts = {
  sans: "'Source Sans Pro', system-ui, sans-serif"
};

const textBase = `
  &:first-child {
    margin-top: 0;
  }
  > *:first-child {
    margin-top: 0;
  }
`;

export const bodyTextBase = `
  ${textBase}
  font-weight: ${fontWeights.normal};
`;

const headlineTextBase = `
  ${textBase}
  font-weight: ${fontWeights.semiBold};
`;

export const h1Text = `
  ${headlineTextBase};
  font-size: ${fontSizesByType.h1[0]}px;
  line-height: 1.125;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.h1[2]}px;
  }
  @media (min-width: ${breakpoints[2]}) {
    font-size: ${fontSizesByType.h1[3]}px;
  }
`;

export const h2Text = `
  ${headlineTextBase};
  font-size: ${fontSizesByType.h2[0]}px;
  line-height: 1.25;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.h2[2]}px;
  }
  @media (min-width: ${breakpoints[2]}) {
    font-size: ${fontSizesByType.h2[3]}px;
  }
`;

export const h3Text = `
  ${headlineTextBase};
  font-size: ${fontSizesByType.h3[0]}px;
  line-height: 1.458333333333333;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.h3[2]}px;
  }
`;

export const h4Text = `
  ${headlineTextBase};
  font-size: ${fontSizesByType.h4[0]}px;
  line-height: 1.75;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.h4[2]}px;
  }
`;

export const h5Text = `
  ${headlineTextBase};
  font-size: ${fontSizesByType.h5[0]}px;
  line-height: 1.875;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.h5[2]}px;
  }
`;

export const h6Text = `
  ${headlineTextBase};
  font-size: ${fontSizesByType.h6[0]}px;
  line-height: 1.875;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.h6[2]}px;
  }
`;

export const bodyLargeText = `
  ${bodyTextBase};
  font-size: ${fontSizesByType.bodyLarge[0]}px;
  line-height: 1.5;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.bodyLarge[2]}px;
  }
`;

export const bodyText = `
  ${bodyTextBase};
  font-size: ${fontSizesByType.body[0]}px;
  line-height: 1.75;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.body[2]}px;
  }
`;

export const bodySmallText = `
  ${bodyTextBase};
  font-size: ${fontSizesByType.bodySmall[0]}px;
  line-height: 1.875;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.bodySmall[2]}px;
  }
  a {
    font-weight: ${fontWeights.semiBold};
  }
`;

export const bodyMicroText = `
  ${bodyTextBase};
  font-size: ${fontSizesByType.bodyMicro[0]}px;
  line-height: 1.875;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.bodyMicro[2]}px;
  }
`;

export const inputText = `
  ${bodyTextBase};
  font-size: ${fontSizesByType.input[0]}px;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.input[2]}px;
  }
`;

export const headingSmallText = `
  ${bodyTextBase};
  font-size: ${fontSizesByType.headingSmall[0]}px;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  line-height: 1.5;
`;

export const underlineText = `
  background-image: linear-gradient(
    to bottom,
    ${colors.dandelion} 0%,
    ${colors.dandelion} 100%
  );
  background-position: 0 0.8em;
  background-repeat: repeat-x;
  background-size: 100% 0.3em;
  text-decoration: none;
  font-weight: ${fontWeights.semiBold};
  font-style: normal;
`;

export const textStyles = {
  h1: h1Text,
  h2: h2Text,
  h3: h3Text,
  h4: h4Text,
  h5: h5Text,
  h6: h6Text,
  headingSmall: headingSmallText,
  bodyLarge: bodyLargeText,
  body: bodyText,
  bodySmall: bodySmallText,
  bodyMicro: bodyMicroText,
  input: inputText,
  underline: underlineText
};

export const richText = css`
  ${textStyles.body};

  * {
    margin-bottom: 0;

    &:first-child {
      margin-top: 0;
    }
  }

  ${[1, 2, 3, 4, 5]
    .map(
      num => `
    h${num} {
      ${textStyles[`h${num}`]}
    }
  `
    )
    .join(' ')}

  h1,
  h2,
  h3 {
    margin-top: 40px;
    + * {
      margin-top: 32px;
    }
    strong,
    u,
    b,
    em {
      ${underlineText};
    }
  }

  h4,
  h5,
  p,
  ul,
  ol {
    margin: 0.75em 0;
    + p,
    + ul,
    + ol,
    + h4,
    + h5 {
      margin-top: 0.75em;
    }
  }

  p,
  ul,
  ol {
    ${textStyles.body};
    font-size: inherit;
    @media (min-width: ${breakpoints[1]}) {
      font-size: inherit;
    }
  }

  ul,
  ol {
    list-style-type: none;
    margin-left: 0;
    padding-left: 0;
    li {
      position: relative;
      padding-left: ${gutterWidth}px;
      &:not(.ql-indent-1):not(.ql-indent-2):not(.ql-indent-3) {
        counter-reset: list-1 list-2 list-3;
      }
      &.ql-indent-1 {
        counter-reset: list-2 list-3;
      }
      &.ql-indent-2 {
        counter-reset: list-3;
      }
      &::before {
        position: absolute;
        left: 0;
        top: 0;
        color: ${colors.waterBlue};
        display: inline-block;
        width: ${(gutterWidth * 3) / 4}px;
        text-align: right;
      }
      ${range(1, 4).map(
        i => `
      &.ql-indent-${i} {
        padding-left: ${gutterWidth * (i + 1)}px;
        &::before {
          left: ${gutterWidth * i}px;
        }
      }`
      )}
    }
  }

  ul {
    li {
      &::before {
        content: '•';
      }
    }
  }

  ol {
    counter-reset: ordered-list;

    li {
      counter-increment: ordered-list;

      &::before {
        content: counter(ordered-list) '.';
      }

      ${range(1, 4).map(
        i => `
      &.ql-indent-${i} {
        counter-increment: list-${i};

        &::before {
          content: counter(list-${i}) '.';
        }
      }`
      )}
    }
  }

  a {
    word-break: break-word;

    &,
    &:active,
    &:visited {
      text-decoration: none;
      color: ${colors.waterBlue};
    }
    &:hover {
      color: ${colors.niceBlue};
      text-decoration: underline;
    }
  }

  strong {
    font-weight: ${fontWeights.semiBold};
  }

  blockquote {
    position: relative;
    width: calc(100% + ${gutterWidth}px);
    margin: 32px -${gutterWidth / 2}px;
    padding: 32px ${gutterWidth / 2}px;
    ${textStyles.bodyLarge};
    text-align: center;
    border-radius: 10px;
    background: ${colors.fog};

    &::before {
      content: '\\201c';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translate(-50%, -35%);
      color: ${colors.dandelion};
      font-size: 100px;
      font-weight: 700;
      line-height: 1;
    }

    &[cite]::after {
      content: attr(cite);
      margin-top: 24px;
      display: block;
      ${textStyles.bodySmall};
    }

    @media (min-width: ${breakpoints[1]}) {
      width: calc(100% + ${gutterWidth * 2}px);
      margin: 64px -${gutterWidth}px;
      padding: 60px ${gutterWidth}px;
    }
  }

  &:first-child {
    margin-top: 0;

    > *:first-child {
      margin-top: 0;
    }
  }
`;

export const richTextEditor = css`
  ${richText};

  font-size: ${fontSizesByType.bodySmall[0]}px;
  @media (min-width: ${breakpoints[1]}) {
    font-size: ${fontSizesByType.bodySmall[2]}px;
  }

  ${[1, 2, 3, 4, 5]
    .map(
      num => css`
        h${num} {
          font-size: ${fontSizesByType[`h${num}`][0] * 0.8}px;
          @media (min-width: ${breakpoints[1]}) {
            font-size: ${fontSizesByType[`h${num}`][0] * 0.8}px;
          }
        }
      `
    )
    .join(' ')}
`;

export default {
  breakpoints,
  space,
  fontSizes,
  fontWeights,
  fonts,
  colors,
  transition
  // shadows
};

type StyleObject = {
  name: UtilityClass,
  styles: Object | string
};

export const utilityClasses: Array<StyleObject> = [
  {
    name: 'flex',
    styles: {
      display: 'flex'
    }
  },
  {
    name: 'align-center',
    styles: {
      alignItems: 'center'
    }
  },
  {
    name: 'justify-between',
    styles: {
      justifyContent: 'space-between'
    }
  },
  {
    name: 'justify-center',
    styles: {
      justifyContent: 'center'
    }
  },
  {
    name: 'rich-text',
    styles: richText
  }
];

export const getClassName = (
  className: string,
  classes?: Array<UtilityClass>
) =>
  `${
    classes
      ? `${classes.map(c => `idlst-${c.replace(/[aeiou-]/g, '')}`).join(' ')}`
      : ''
  } ${className}`;

export const globalStyles = `
  body {
    margin: 0;
    box-sizing: border-box;
  }

  body .pac-container {
    z-index: 100001;
  }

  button {
    padding: 0;
    appearance: none;
    border: none;
    background-color: transparent;
    cursor: pointer;
    color: inherit;
  }

  a {
    color: #0d75de;
  }

  a:hover {
    color: #0f5cb5;
  }`;
